import { Chart as ChartJS } from "chart.js";

import React, { useCallback, useEffect, useState, useRef } from "react";
import { GraphData } from "../types";
import { LegendItem } from "../CustomLegend";

const useChartLegend = (
  chartRef: React.RefObject<ChartJS<"line">>,
  chartData: GraphData,
) => {
  const [legendItems, setLegendItems] = useState<LegendItem[]>([]);
  const [hiddenDatasets, setHiddenDatasets] = useState<Record<number, boolean>>(
    {},
  );
  const prevChartRef = useRef<ChartJS<"line"> | null>(null);

  const updateLegendItems = useCallback(() => {
    if (!chartRef.current) return;

    const chart = chartRef.current;
    const items = chart.legend?.legendItems || [];

    const updatedItems = items.map((item) => {
      const datasetIndex =
        item.datasetIndex !== undefined ? item.datasetIndex : 0;
      const dataset = chartData.datasets[datasetIndex];

      const meta = chart.getDatasetMeta(datasetIndex);
      const isHidden = meta?.hidden || false;

      if (isHidden !== !!hiddenDatasets[datasetIndex]) {
        setTimeout(() => {
          setHiddenDatasets((prev) => ({
            ...prev,
            [datasetIndex]: isHidden,
          }));
        }, 0);
      }

      return {
        ...item,
        hidden: isHidden,
        reportName: dataset?.reportName,
        datasetId: dataset?.datasetId,
        datasetName: dataset?.datasetName,
      } as LegendItem;
    });

    setLegendItems(updatedItems);
  }, [hiddenDatasets, chartData.datasets, chartRef]);

  useEffect(() => {
    if (chartData.datasets.length > 0) {
      setHiddenDatasets({});
    }
  }, [chartData.datasets]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const currentChart = chartRef.current;

      if (currentChart && currentChart !== prevChartRef.current) {
        prevChartRef.current = currentChart;
        updateLegendItems();
        clearInterval(intervalId);
      }
    }, 200);

    return () => {
      clearInterval(intervalId);
    };
  }, [chartRef, updateLegendItems]);

  useEffect(() => {
    if (!chartRef.current || chartData.datasets.length === 0) return;

    let needsUpdate = false;
    Object.entries(hiddenDatasets).forEach(([indexStr, isHidden]) => {
      const index = Number(indexStr);
      const meta = chartRef.current?.getDatasetMeta(index);

      if (meta && meta.hidden !== isHidden) {
        meta.hidden = isHidden;
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      chartRef.current?.update();
    }

    const timeoutId = setTimeout(() => {
      updateLegendItems();
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [hiddenDatasets, chartData.datasets, updateLegendItems, chartRef]);

  const handleLegendItemClick = useCallback((datasetIndex: number) => {
    if (datasetIndex === undefined) return;

    setHiddenDatasets((prev) => {
      const newHiddenState = !prev[datasetIndex];
      return {
        ...prev,
        [datasetIndex]: newHiddenState,
      };
    });
  }, []);

  return {
    legendItems,
    updateLegendItems,
    handleLegendItemClick,
  };
};

export default useChartLegend;
